// Self-contained "Members list" widget. Composes the filter-members feature
// (chip strip + selection store), the upgrade-plan feature (FREE-tier
// tease), and the member entity (rows + badges).
//
// This widget does no data fetching itself — the page above passes in the
// already-resolved infinite-query slice. That split keeps the widget
// render-pure (re-renders cheaply when only one page lands) and lets future
// surfaces (a desktop two-pane view, a CSV export) reuse it without the
// React Query plumbing.

import type {
  MemberLifecycleStage,
  MemberListItem,
  MemberStageCounts,
} from "@community-analytics/shared";

import { MemberRow } from "@entities/member";
import { StageFilterChips } from "@features/filter-members";
import { UpgradeTease } from "@features/upgrade-plan";
import { cn, useIntersection } from "@shared/lib";
import { Button } from "@shared/ui";

import { MEMBERS_STRINGS } from "../lib/strings";

import { MembersEmptyState } from "./MembersEmptyState";

export interface MembersPanelProps {
  /** Plan-aware response: when upgradeRequired=true, render the tease only. */
  upgradeRequired: boolean;
  /** Concatenated rows across every infinite-query page so far. */
  items: ReadonlyArray<MemberListItem>;
  /** Per-stage counts (always returned by the API). */
  counts: MemberStageCounts;
  /** Currently selected stages — drives empty-state copy + chip pressed state. */
  selectedStages: ReadonlyArray<MemberLifecycleStage>;
  /** True while the next page is in flight — disables the load-more sentinel. */
  isFetchingNextPage: boolean;
  /** True when there is a next page to fetch — gates the sentinel + button. */
  hasNextPage: boolean;
  /** Fetcher invoked by the IntersectionObserver sentinel and the explicit button. */
  onLoadMore: () => void;
  /** FREE-tier upgrade CTA — wired in TASK-036. Disabled when omitted. */
  onUpgrade?: () => void;
  className?: string;
}

export function MembersPanel({
  upgradeRequired,
  items,
  counts,
  selectedStages,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onUpgrade,
  className,
}: MembersPanelProps) {
  // The intersection sentinel triggers `onLoadMore` ~200px before it scrolls
  // into view. The hook is gated by `hasNextPage && !isFetchingNextPage` so
  // we don't spam fetches while one is in flight or at end-of-list.
  const sentinelRef = useIntersection(onLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
  });

  if (upgradeRequired) {
    return (
      <div className={cn("flex flex-col gap-4", className)}>
        <UpgradeTease
          counts={counts}
          labelsByStage={MEMBERS_STRINGS.stageLabels}
          title={MEMBERS_STRINGS.upgrade.title}
          body={MEMBERS_STRINGS.upgrade.body}
          ctaLabel={MEMBERS_STRINGS.upgrade.cta}
          {...(onUpgrade ? { onUpgrade } : {})}
        />
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <StageFilterChips
        counts={counts}
        labelsByStage={MEMBERS_STRINGS.stageLabels}
        ariaLabel={MEMBERS_STRINGS.filterAria}
      />
      {items.length === 0 ? (
        <MembersEmptyState
          title={MEMBERS_STRINGS.empty.title}
          body={emptyBody(selectedStages)}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              stageLabel={MEMBERS_STRINGS.stageLabels[member.lifecycleStage]}
              lurkerLabel={MEMBERS_STRINGS.lurkerLabel}
              unknownNameLabel={MEMBERS_STRINGS.unknownName}
            />
          ))}
        </ul>
      )}
      {/* Sentinel — invisible, but the IntersectionObserver fires the
          prefetch when it scrolls into the rootMargin window. We still
          render an explicit button as a fallback for keyboard users and
          for browsers without IntersectionObserver. */}
      {hasNextPage ? (
        <div ref={sentinelRef} className="flex justify-center pt-2">
          <Button
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            variant="outline"
            size="sm"
          >
            {isFetchingNextPage
              ? MEMBERS_STRINGS.loadingMore
              : MEMBERS_STRINGS.loadMore}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Empty-state body: when exactly one stage chip is selected, surface the
 * stage-specific message ("No AT_RISK members — great news!"). Otherwise
 * fall back to the generic copy.
 */
function emptyBody(stages: ReadonlyArray<MemberLifecycleStage>): string {
  if (stages.length === 1) {
    const only = stages[0];
    if (only) return MEMBERS_STRINGS.emptyByStage[only];
  }
  return MEMBERS_STRINGS.empty.body;
}
